// XXX(chang): this is a copy of ui/app/service/settingsPage/ApiKeysCard.tsx
// which is highly coupled to services. We should think about how to better
// abstract this

import { ApolloError, gql, useApolloClient, useMutation } from '@apollo/client';
import IconOverflow from '@apollo/icons/small/IconOverflow.svg';
import {
  Alert,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuContent,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@apollo/orbit';
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Code } from 'src/components/code/Code';
import { Form } from 'src/components/common/form/Form';
import { URLModal } from 'src/components/common/URLModal';
import Config from 'src/lib/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { setOverlayVisibleLocation } from 'src/lib/routing';

export const UserApiKeysCardFragment = gql`
  fragment UserApiKeysCardFragment on User {
    id
    apiKeys {
      id
      keyName
    }
  }
`;

interface Props {
  user: GraphQLTypes.UserApiKeysCardFragment;
  setError: (e: ApolloError) => void;
}
export const UserApiKeysCard = ({ user, setError }: Props) => {
  const location = useLocation();
  const client = useApolloClient();
  const [newKey, setNewKey] =
    useState<GraphQLTypes.GenerateUserAPIKeyMutation_user_newKey>();
  const [addKeyMutation, { loading }] = useMutation<
    GraphQLTypes.GenerateUserAPIKeyMutation,
    GraphQLTypes.GenerateUserAPIKeyMutationVariables
  >(
    gql`
      mutation GenerateUserAPIKeyMutation($userId: ID!, $keyName: String!) {
        user(id: $userId) {
          user {
            id
          }
          newKey(keyName: $keyName) {
            id
            token
            keyName
          }
        }
      }
    `,
    {
      onCompleted: (data) => {
        client.cache.evict({
          id: client.cache.identify({
            __typename: data.user?.user.__typename,
            id: data.user?.user.id,
          }),
          fieldName: 'apiKeys',
        });
        setNewKey(data.user?.newKey);
      },
      onError: (e) => setError(e),
    },
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Personal API key</CardTitle>
        <CardDescription>
          These keys are unique to you and match your personal permissions.
          <br />
          To configure CI/CD environments, we recommend using{' '}
          <em>graph-level</em> API keys located in the settings page of your
          graph.
        </CardDescription>
        <Button
          variant="secondary"
          isDisabled={loading}
          as={Link}
          to={setOverlayVisibleLocation({
            location,
            overlayName: Config.modals.newUserKey,
          })}
        >
          Create new key
        </Button>
      </CardHeader>
      <CardBody>
        <URLModal name={Config.modals.newUserKey}>
          {({ isOpen, closeModal }) => (
            <Modal
              isOpen={isOpen}
              onClose={() => {
                closeModal();
              }}
              size="xl"
            >
              <ModalOverlay />
              <Form
                onSubmit={async (event) => {
                  event.preventDefault();

                  const keyName = new FormData(event.currentTarget).get(
                    'keyName',
                  );
                  if (typeof keyName !== 'string') {
                    throw new Error('Could not get `name` field from form');
                  }

                  await addKeyMutation({
                    variables: { userId: user.id, keyName },
                  });
                }}
              >
                <ModalContent>
                  <ModalHeader>
                    <ModalTitle>New API Key</ModalTitle>
                  </ModalHeader>
                  <ModalBody>
                    {newKey ? (
                      <>
                        <Alert status="info">
                          Make sure to copy your API key now. You won't be able
                          to see it again.
                        </Alert>
                        <Code
                          showCopyButton
                          code={newKey.token}
                          className="flex justify-between rounded p-3"
                        />
                      </>
                    ) : (
                      <FormControl>
                        <FormLabel>Key name</FormLabel>
                        <Input name="keyName" />
                      </FormControl>
                    )}
                  </ModalBody>
                  <ModalFooter>
                    <ButtonGroup>
                      {newKey ? (
                        <Button
                          variant="primary"
                          onClick={() => {
                            closeModal();
                            setNewKey(undefined);
                          }}
                        >
                          Close
                        </Button>
                      ) : (
                        <>
                          <Button
                            variant="secondary"
                            type="button"
                            isDisabled={loading}
                            onClick={() => {
                              closeModal();
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="primary"
                            isLoading={loading}
                            loadingText="Create"
                            type="submit"
                          >
                            Create
                          </Button>
                        </>
                      )}
                    </ButtonGroup>
                  </ModalFooter>
                </ModalContent>
              </Form>
            </Modal>
          )}
        </URLModal>
        <TableContainer>
          <Table>
            <colgroup>
              <col />
              <col width="1%" />
            </colgroup>
            <Thead>
              <Tr>
                <Th>Name</Th>
                <Th>
                  <span className="sr-only">Actions</span>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {(user.apiKeys || []).map(({ keyName, id }) => (
                <Tr key={id}>
                  <Td className="whitespace-nowrap">{keyName}</Td>
                  <Td isActionsMenu>
                    <UserActionMenu
                      userId={user.id}
                      apiKey={{ keyName, id }}
                      setError={setError}
                    />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </CardBody>
    </Card>
  );
};

interface UserActionMenuProps {
  userId: string;
  apiKey: { keyName: string | null; id: string };
  setError: (e: ApolloError) => void;
}
const UserActionMenu = ({ userId, apiKey, setError }: UserActionMenuProps) => {
  const client = useApolloClient();
  const [idOfKeyToRename, setIdOfKeyToRename] = useState<string | null>(null);
  const [renameKeyMutation, { loading: renameLoading }] = useMutation<
    GraphQLTypes.RenameUserAPIKeyMutation,
    GraphQLTypes.RenameUserAPIKeyMutationVariables
  >(
    gql`
      mutation RenameUserAPIKeyMutation(
        $userId: ID!
        $id: ID!
        $newKey: String!
      ) {
        user(id: $userId) {
          user {
            id
          }
          renameKey(id: $id, newKeyName: $newKey) {
            id
            token
            keyName
          }
        }
      }
    `,
    {
      onCompleted: (data) => {
        setIdOfKeyToRename(null);

        client.cache.evict({
          id: client.cache.identify({
            __typename: data.user?.user.__typename,
            id: data.user?.user.id,
          }),
          fieldName: 'apiKeys',
        });
      },
      onError: (e) => setError(e),
    },
  );
  const [idOfKeyToRemove, setIdOfKeyToRemove] = useState<string | null>(null);
  const [removeKeyMutation, { loading: removeLoading }] = useMutation<
    GraphQLTypes.RemoveUserAPIKeyMutation,
    GraphQLTypes.RemoveUserAPIKeyMutationVariables
  >(
    gql`
      mutation RemoveUserAPIKeyMutation($userId: ID!, $id: ID!) {
        user(id: $userId) {
          user {
            id
          }
          removeKey(id: $id)
        }
      }
    `,
    {
      onCompleted: (data) => {
        client.cache.evict({
          id: client.cache.identify({
            __typename: data.user?.user.__typename,
            id: data.user?.user.id,
          }),
          fieldName: 'apiKeys',
        });
      },
      onError: (e) => setError(e),
    },
  );

  return (
    <>
      <Menu placement="bottom-end" isLazy autoSelect={false}>
        <MenuButton as={IconButton} variant="secondary">
          <IconOverflow />
        </MenuButton>
        <MenuContent>
          <MenuList>
            <MenuItem
              onClick={() => {
                setIdOfKeyToRename(apiKey.id);
              }}
            >
              Rename Token
            </MenuItem>
            <MenuItem
              className="text-error hover:bg-error hover:text-error"
              onClick={() => {
                setIdOfKeyToRemove(apiKey.id);
              }}
            >
              Delete Token
            </MenuItem>
          </MenuList>
        </MenuContent>
      </Menu>

      {idOfKeyToRename && (
        <Modal isOpen onClose={() => setIdOfKeyToRename(null)} size="xl">
          <ModalOverlay />
          <Form
            noValidate
            onSubmit={(event) => {
              event.preventDefault();

              const newKey = new FormData(event.currentTarget).get('newKey');
              if (typeof newKey !== 'string')
                throw new Error('Could not find newKey element in form');

              renameKeyMutation({
                variables: {
                  userId,
                  id: idOfKeyToRename,
                  newKey,
                },
              });
            }}
          >
            <ModalContent>
              <ModalHeader>
                <ModalTitle>Rename API Key</ModalTitle>
              </ModalHeader>
              <ModalBody>
                <FormControl>
                  <FormLabel>Key name</FormLabel>
                  <Input
                    defaultValue={apiKey.keyName || ''}
                    isDisabled={renameLoading}
                    name="newKey"
                  />
                </FormControl>
              </ModalBody>
              <ModalFooter>
                <ButtonGroup>
                  <Button
                    variant="secondary"
                    isDisabled={renameLoading}
                    onClick={() => setIdOfKeyToRename(null)}
                    type="button"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    isLoading={renameLoading}
                    loadingText="Rename"
                    type="submit"
                  >
                    Rename
                  </Button>
                </ButtonGroup>
              </ModalFooter>
            </ModalContent>
          </Form>
        </Modal>
      )}
      {idOfKeyToRemove && (
        <Modal isOpen onClose={() => setIdOfKeyToRemove(null)} size="xl">
          <ModalOverlay />
          <Form
            onSubmit={async (event) => {
              event.preventDefault();

              await removeKeyMutation({
                variables: { id: idOfKeyToRemove, userId },
              });

              setIdOfKeyToRemove(null);
            }}
          >
            <ModalContent>
              <ModalHeader>
                <ModalTitle>
                  {apiKey.keyName !== ''
                    ? `Are you sure you want to delete the API Key "${apiKey.keyName}"?`
                    : 'Are you sure you want to delete this unnamed API Key?'}
                </ModalTitle>
              </ModalHeader>
              <ModalBody>
                If you delete this API Key, projects using this key will no
                longer be able to interact with Studio.
              </ModalBody>
              <ModalFooter>
                <ButtonGroup>
                  <Button
                    variant="secondary"
                    isDisabled={removeLoading}
                    type="button"
                    onClick={() => setIdOfKeyToRemove(null)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    isLoading={removeLoading}
                    loadingText="Yes, delete key"
                    type="submit"
                  >
                    Yes, delete key
                  </Button>
                </ButtonGroup>
              </ModalFooter>
            </ModalContent>
          </Form>
        </Modal>
      )}
    </>
  );
};
